import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";
import { fetchEnumsFromSchema } from "@/core/tools/forms/fetch_enums_from_schema";

interface WashingsReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date[];
  };

  documents: any[];
  schema: any;
  meta: any;
}

const state: WashingsReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    targetDates: [new Date(), new Date()]
  },

  documents: [],
  schema: {},
  meta: {}
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates[0]).format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates[1])
      .add(1, "day")
      .format("YYYY-MM-DD");
  }

  if (params.dZoneGroups) {
    queryParams.delivery_zone_id = params.dZoneGroups;
  }
  if (params.employeeId) {
    queryParams.employee_id = params.employeeId;
  }

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";
export const GET_SCHEMA_FACT_VDP = "GET_SCHEMA_FACT_VDP";

export const actions: ActionTree<WashingsReportState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("documents.list")
            .action({
              columns: {
                id: "s.id",
                shift_date: "s.shift_date",
                shift_id: "s.id",
                times_of_day: "s.times_of_day",
                number: "spw.number",
                product_name: "vv.name",
                wash_type: "vwt.name",
                delay: "vvw.delay",
                begin_datetime: "sjw.begin_datetime",
                end_datetime: "sjw.end_datetime",
                workers: "sw.workers"
              },
              from: "s:shifts",
              from_as: "s",
              joins: [
                {
                  join: "s:shift_plan_washing",
                  type: "left",
                  as: "spw",
                  on: {
                    $eq: ["spw.shift_id", "s.id"]
                  }
                },
                {
                  join: "s:shift_journal_washing",
                  type: "left",
                  as: "sjw",
                  on: {
                    $eq: ["sjw.plan_washing_id", "spw.id"]
                  }
                },
                {
                  join: "s:voc_volumes",
                  as: "vv",
                  on: {
                    $eq: ["vv.id", "spw.equipment_id"]
                  }
                },
                {
                  join: "s:voc_volumes_washings",
                  as: "vvw",
                  on: {
                    $eq: ["vvw.id", "spw.washing_id"]
                  }
                },
                {
                  join: "s:washings",
                  as: "w",
                  on: {
                    $eq: ["w.id", "vvw.washing_id"]
                  }
                },
                {
                  join: "s:voc_washing_types",
                  as: "vwt",
                  on: {
                    $eq: ["vwt.id", "w.type_id"]
                  }
                },
                {
                  join: {
                    columns: {
                      shift_id: "sw.shift_id",
                      workers: {
                        $array_agg: {
                          $obj: {
                            name: "e.name",
                            is_manager: "vr.is_manager",
                            loss_rate: "e.loss_rate"
                          }
                        }
                      }
                    },
                    from: "s:shift_workers",
                    from_as: "sw",
                    joins: [
                      {
                        join: "s:voc_roles",
                        type: "left",
                        as: "vr",
                        on: {
                          $and: [{ $eq: ["sw.role_id", "vr.id"] }]
                        }
                      },
                      {
                        join: "s:employees",
                        type: "left",
                        as: "e",
                        on: {
                          $eq: ["sw.employee_id", "e.id"]
                        }
                      }
                    ],
                    groups: ["sw.shift_id"]
                  },
                  type: "left",
                  as: "sw",
                  on: {
                    $eq: ["sw.shift_id", "s.id"]
                  }
                }
              ],
              page_size: 10000,
              where: {
                $and: [
                  { $gte: ["s.shift_date", { $: params.targetDates[0] }] },
                  { $lte: ["s.shift_date", { $: params.targetDates[1] }] }
                  // {$not: [{$in: ['spv.number', {$: ['0Н', '0Д']}]}]},
                ]
              },
              order: [{ $asc: ["s.shift_date"] }]
            }),
          vm
            .$q(GET_SCHEMA_FACT_VDP)
            .raw("schemas.get")
            .action({
              id: `s:shifts`
            })
        ]).then((result: ServerResult) => {
          const content = result.getOperation(REPORT_DATA).getContent();
          const schema = result.getOperation(GET_SCHEMA_FACT_VDP).getContent()
            .schema;
          commit("SET_REPORT_DATA", {
            data: content.documents.map((item: any) => {
              const managers = item.workers.filter((item: any) => {
                return item.is_manager;
              });
              item.manager_name = managers.length ? managers[0].name : "";
              const operators = item.workers.filter((item: any) => {
                return !item.is_manager;
              });
              item.operator_name = operators.length ? operators[0].name : "";
              if (item.begin_datetime && item.end_datetime) {
                const minutes = moment(item.end_datetime).diff(
                  moment(item.begin_datetime),
                  "minutes"
                );
                item.fact_delay = moment()
                  .startOf("day")
                  .add(minutes, "minutes");
                item.fact_delay_orig = minutes;
              }
              item.delay_orig = item.delay.secs / 60;
              item.delay = moment()
                .startOf("day")
                .add(item.delay.secs, "seconds");
              if (item.fact_delay) {
                const diff = item.delay.isBefore(item.fact_delay)
                  ? moment(item.fact_delay).diff(moment(item.delay), "minutes")
                  : moment(item.delay).diff(moment(item.fact_delay), "minutes");
                item.delay_diff = moment()
                  .startOf("day")
                  .add(diff, "minutes");
                item.delay_diff_sign = item.delay.isBefore(item.fact_delay)
                  ? "+"
                  : "-";
                item.delay_diff_danger = Math.abs(diff) > 15;
              }

              return item;
            }),
            schema
          });
          commit("LOADED_SUCCESS");
          resolve();
        });
      } else {
        resolve();
      }
    });
  },

  exportDataAsXLSX({ rootState }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("reports.documents.export")
          .action({ ...buildFilter(params), format: "xlsx" })
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  }
};

export const mutations: MutationTree<WashingsReportState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, { data, schema }) {
    state.documents = data;
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);
  }
};

const getters = {
  total: (state: WashingsReportState) => {
    return state.documents.reduce(
      (acc: any, item: any) => {
        console.log(item);
        acc.delay_orig += item.delay_orig || 0;
        if (acc.delay_orig) {
          acc.delay = moment()
            .startOf("day")
            .add(acc.delay_orig, "minutes");
        }
        acc.fact_delay_orig += item.fact_delay_orig || 0;
        acc.fact_delay = moment()
          .startOf("day")
          .add(acc.fact_delay_orig, "minutes");
        return acc;
      },
      {
        delay_orig: 0,
        delay: 0,
        fact_delay_orig: 0,
        fact_delay: 0
      }
    );
  },
  exportData(state: WashingsReportState) {
    const csvContent: any[] = [];
    const header = [
      "Дата",
      "День/Ночь",
      "Мастер",
      "Аппаратчик",
      "Оборудование",
      "Тип мойки",
      "Плановая продолжительность мойки",
      "Фактическая продолжительность мойки",
      "Отклонение от плановой продолжительности мойки, часы и мин"
    ];

    state.documents.map((item: any, idx: number) => {
      const row: any[] = [];
      row.push(
        item.shift_date ? moment(item.shift_date).format("DD.MM.YYYY") : ""
      );
      row.push(
        state.meta.times_of_day && state.meta.times_of_day.choices
          ? state.meta.times_of_day.choices[item.times_of_day]
          : ""
      );
      row.push(item.manager_name || "");
      row.push(item.operator_name || "");
      row.push(item.product_name || "");
      row.push(item.wash_type || "");
      row.push(item.delay ? item.delay.format("HH:mm") : "");
      row.push(item.fact_delay ? item.fact_delay.format("HH:mm") : "");
      row.push(
        item.delay_diff
          ? (item.delay_diff_sign || "") + item.delay_diff.format("HH:mm")
          : ""
      );
      csvContent.push(row);
    });
    return [header, ...csvContent];
  }
};

export const STORE_KEY = "$_washings_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
